import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import {
  FileText,
  ShieldCheck,
  CreditCard,
  Scale,
  Award,
  Lock,
  AlertTriangle,
  RefreshCw,
  Mail,
  Phone,
  MapPin,
  CheckCircle2,
  HelpCircle,
  Briefcase
} from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const TermsOfServicePage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const lastUpdated = "August 16, 2026";

  const termsSections = [
    {
      id: "acceptance-of-terms",
      title: "1. Acceptance of Terms & Engagement Framework",
      icon: FileText,
      content: (
        <div className="space-y-4">
          <p>
            These Terms of Service ("Terms", "Agreement") constitute a legally binding agreement between you or the entity you represent ("Client", "Partner", "you") and <strong className="text-white">Stellars Estates</strong> ("Company", "Agency", "we", "our", or "us"), founded by <strong className="text-white">Sahil Sharma</strong> with operational headquarters in <strong className="text-white">Delhi/NCR, India</strong>.
          </p>
          <p>
            By accessing our website (<a href="https://stellarsestates.in" className="text-[#F3453F] hover:underline">stellarsestates.in</a> / <a href="https://stellarsestates.com" className="text-[#F3453F] hover:underline">stellarsestates.com</a>), engaging our growth marketing consulting services, signing a Master Services Agreement (MSA) or Statement of Work (SOW), or commissioning custom software and AI workflows, you explicitly acknowledge and agree to be bound by these Terms.
          </p>
        </div>
      )
    },
    {
      id: "scope-of-services",
      title: "2. Scope of Services & Deliverables",
      icon: Briefcase,
      content: (
        <div className="space-y-3">
          <p>Stellars Estates provides specialized enterprise growth infrastructure across five primary service pillars:</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs sm:text-sm">
            <div className="p-3.5 rounded-xl bg-white/5 border border-white/10 space-y-1">
              <p className="font-bold text-white flex items-center gap-1.5">
                <CheckCircle2 size={14} className="text-[#F3453F]" />
                <span>Performance Marketing & Paid Media</span>
              </p>
              <p className="text-white/90 text-xs">Algorithmic media buying across Meta Ads, Google Ads (PPC/PMax), YouTube, and programmatic OTT channels.</p>
            </div>
            <div className="p-3.5 rounded-xl bg-white/5 border border-white/10 space-y-1">
              <p className="font-bold text-white flex items-center gap-1.5">
                <CheckCircle2 size={14} className="text-[#F3453F]" />
                <span>Full-Stack Web & Software Engineering</span>
              </p>
              <p className="text-white/90 text-xs">High-converting React/Vite landing pages, dynamic financing calculators, and speed-optimized web architectures.</p>
            </div>
            <div className="p-3.5 rounded-xl bg-white/5 border border-white/10 space-y-1">
              <p className="font-bold text-white flex items-center gap-1.5">
                <CheckCircle2 size={14} className="text-[#F3453F]" />
                <span>Autonomous AI Automation & CRM</span>
              </p>
              <p className="text-white/90 text-xs">WhatsApp Business API bots, natural language qualification engines, and bi-directional CRM synchronizations.</p>
            </div>
            <div className="p-3.5 rounded-xl bg-white/5 border border-white/10 space-y-1">
              <p className="font-bold text-white flex items-center gap-1.5">
                <CheckCircle2 size={14} className="text-[#F3453F]" />
                <span>Search Dominance (SEO, AEO, GEO)</span>
              </p>
              <p className="text-white/90 text-xs">Topical authority clustering, JSON-LD Knowledge Graph schemas, and generative AI answer engine optimization.</p>
            </div>
          </div>
        </div>
      )
    },
    {
      id: "media-spend-billing",
      title: "3. Direct Ad Spend Billing & Media Budget Governance",
      icon: CreditCard,
      content: (
        <div className="space-y-4 text-xs sm:text-sm text-white/80">
          <p>
            To ensure complete financial transparency and eliminate markup risks:
          </p>
          <ul className="list-disc list-inside space-y-2 pl-2">
            <li><strong className="text-white">Direct Ad Network Invoicing:</strong> All third-party media ad spend (Meta, Google, YouTube, LinkedIn) is paid directly by the Client to the respective ad networks using the Client's corporate payment methods.</li>
            <li><strong className="text-white">Agency Retainer Separation:</strong> Stellars Estates fee structure is strictly isolated to professional management retainers, software engineering sprint fees, or agreed performance revenue shares.</li>
            <li><strong className="text-white">Budget Authorization:</strong> No campaign budget increase or media deployment will occur without prior written or electronic approval from the Client's designated point of contact.</li>
          </ul>
        </div>
      )
    },
    {
      id: "intellectual-property",
      title: "4. Intellectual Property & 100% Asset Ownership",
      icon: Award,
      content: (
        <div className="space-y-4 text-xs sm:text-sm text-white/80">
          <div className="p-4 rounded-xl bg-[#F3453F]/10 border border-[#F3453F]/30 text-white space-y-2">
            <p className="text-sm font-bold text-white flex items-center gap-2">
              <ShieldCheck size={16} className="text-[#F3453F]" />
              <span>Full Client Asset Ownership Guarantee</span>
            </p>
            <p className="text-xs text-white/90">
              Upon settlement of invoice retainers, the Client retains exclusive, unrestricted ownership of all campaign deliverables, ad creatives, copy assets, custom landing page codebases, lead lists, and customer CRM records created during the partnership.
            </p>
          </div>
          <p>
            <strong className="text-white">Agency Proprietary Tools:</strong> Stellars Estates retains ownership of its internal operating procedures, proprietary automation scripts, pre-built backend algorithmic templates, and general analytical frameworks developed independently of specific client deliverables.
          </p>
        </div>
      )
    },
    {
      id: "client-obligations",
      title: "5. Client Obligations & Speed-to-Lead Partnership",
      icon: CheckCircle2,
      content: (
        <div className="space-y-3 text-xs sm:text-sm text-white/80">
          <p>Successful performance marketing is a collaborative partnership. The Client agrees to:</p>
          <ul className="list-disc list-inside space-y-1.5 pl-2">
            <li>Provide accurate brand guidelines, project pricing matrices, and sales collateral in a timely manner.</li>
            <li>Maintain active payment methods on all connected ad accounts to prevent campaign disruption.</li>
            <li>Operate a disciplined sales follow-up process, utilizing the real-time WhatsApp and CRM lead notifications configured by our engineering team to ensure rapid speed-to-lead response times.</li>
            <li>Provide prompt feedback on campaign creative previews during weekly sprint cycles.</li>
          </ul>
        </div>
      )
    },
    {
      id: "payment-terms",
      title: "6. Retainers, Invoicing & Commercial Terms",
      icon: CreditCard,
      content: (
        <div className="space-y-3 text-xs sm:text-sm text-white/80">
          <ul className="list-disc list-inside space-y-2 pl-2">
            <li><strong className="text-white">Invoicing Cycle:</strong> Agency retainer invoices are issued in advance on a monthly or agreed project milestone basis and are payable within 7 calendar days of receipt.</li>
            <li><strong className="text-white">Taxes & GST:</strong> Applicable Goods & Services Tax (GST 18%) is levied on all professional agency fees in accordance with the statutory laws of India.</li>
            <li><strong className="text-white">Late Payments:</strong> Overdue invoices exceeding 15 days may result in a temporary pause of active campaign management until accounts are settled in full.</li>
          </ul>
        </div>
      )
    },
    {
      id: "performance-disclaimers",
      title: "7. Performance Disclaimers & Third-Party Platforms",
      icon: AlertTriangle,
      content: (
        <div className="space-y-3 text-xs sm:text-sm text-white/80">
          <p>
            While Stellars Estates applies rigorous scientific multivariate testing, data engineering, and proven conversion frameworks that consistently generate industry-leading ROAS:
          </p>
          <ul className="list-disc list-inside space-y-1.5 pl-2">
            <li>We do not guarantee specific monetary sales figures or final conversion closes, as final sales conversion depends inherently on client sales team execution, product pricing, market competition, and inventory availability.</li>
            <li>We are not liable for arbitrary third-party platform policy shifts, algorithm modifications, account reviews, or system outages enacted independently by Meta, Google, WhatsApp/Meta, or LinkedIn.</li>
          </ul>
        </div>
      )
    },
    {
      id: "confidentiality",
      title: "8. Confidentiality & Non-Disclosure",
      icon: Lock,
      content: (
        <div className="space-y-3 text-xs sm:text-sm text-white/80">
          <p>
            Both parties agree to treat all business data, customer lists, financial metrics, advertising performance data, and proprietary business methods as strictly confidential. Neither party will disclose confidential information to any third party without explicit prior written consent.
          </p>
        </div>
      )
    },
    {
      id: "termination-and-transition",
      title: "9. Term, Cancellation & Offboarding Protocols",
      icon: RefreshCw,
      content: (
        <div className="space-y-3 text-xs sm:text-sm text-white/80">
          <p>
            Either party may terminate a month-to-month retainer agreement by providing <strong className="text-white">30 days advance written notice</strong> via email to <code className="text-[#F3453F]">connect@stellarsestates.in</code>.
          </p>
          <p>
            Upon completion of the notice period and full settlement of outstanding invoices, Stellars Estates will complete a seamless offboarding transition, ensuring all administrative accesses and assets remain seamlessly in the Client's control.
          </p>
        </div>
      )
    },
    {
      id: "governing-law",
      title: "10. Governing Law & Dispute Resolution",
      icon: Scale,
      content: (
        <div className="space-y-3 text-xs sm:text-sm text-white/80">
          <p>
            These Terms shall be governed by and construed in accordance with the substantive laws of <strong className="text-white">India</strong>.
          </p>
          <p>
            Any dispute, controversy, or claim arising out of or relating to these Terms shall first be attempted to be resolved amicably through good-faith senior executive negotiations. In the event negotiations fail, disputes shall be subject to the exclusive jurisdiction of the competent courts in <strong className="text-white">Delhi/NCR, India</strong>.
          </p>
        </div>
      )
    },
    {
      id: "contact-notices",
      title: "11. Official Contact & Notices",
      icon: Mail,
      content: (
        <div className="p-5 rounded-2xl bg-white/5 border border-white/10 space-y-4">
          <p className="text-xs sm:text-sm text-white/85">
            For formal legal notices, contract inquiries, or partnership governance, please contact:
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs sm:text-sm">
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-white">
                <MapPin size={16} className="text-[#F3453F]" />
                <span className="font-bold">Stellars Estates</span>
              </div>
              <p className="text-white/70 pl-6">Delhi/NCR, India</p>

              <div className="flex items-center gap-2 text-white pt-2">
                <Phone size={16} className="text-[#F3453F]" />
                <span className="font-bold">Telephone / WhatsApp</span>
              </div>
              <p className="text-white/70 pl-6">+91 93109 71677</p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2 text-white">
                <Mail size={16} className="text-[#F3453F]" />
                <span className="font-bold">Corporate Communications</span>
              </div>
              <p className="text-white/70 pl-6">
                <a href="mailto:connect@stellarsestates.in" className="text-[#F3453F] font-bold hover:underline">
                  connect@stellarsestates.in
                </a>
              </p>

              <div className="flex items-center gap-2 text-white pt-2">
                <Briefcase size={16} className="text-[#F3453F]" />
                <span className="font-bold">Executive Office</span>
              </div>
              <p className="text-white/70 pl-6">Raj Nagar Extension, Ghaziabad</p>
            </div>
          </div>
        </div>
      )
    }
  ];

  return (
    <>
      <Helmet>
        <title>Terms of Service | Stellars Estates Digital Marketing Agency</title>
        <meta
          name="description"
          content="Review the official Terms of Service of Stellars Estates. Understand our performance marketing agreements, client asset ownership guarantees, billing terms, and SLAs."
        />
        <link rel="canonical" href="https://stellarsestates.in/terms-of-service" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Terms of Service | Stellars Estates Digital Marketing Agency" />
        <meta property="og:description" content="Review the official Terms of Service of Stellars Estates. Understand our performance marketing agreements, client asset ownership guarantees, billing terms, and SLAs." />
        <meta property="og:image" content="https://i.postimg.cc/3NNd39Q1/social-sharing-image-stallars-estates.jpg" />
        <meta property="og:image:secure_url" content="https://i.postimg.cc/3NNd39Q1/social-sharing-image-stallars-estates.jpg" />
        <meta property="og:image:alt" content="Stellars Estates Terms of Service" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://i.postimg.cc/3NNd39Q1/social-sharing-image-stallars-estates.jpg" />
      </Helmet>

      <div className="min-h-screen bg-[#181F32]">
        <Header />

        <main className="pt-12 pb-24">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
            
            {/* Header Title Section */}
            <div className="glass-panel-hero p-8 sm:p-12 text-center space-y-4">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#F3453F]/15 border border-[#F3453F]/40 text-[#F3453F] text-xs font-bold tracking-wider uppercase">
                <Scale size={14} />
                <span>Commercial & Service Agreement</span>
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white font-heading">
                Terms of <span className="text-[#F3453F]">Service</span>
              </h1>
              <p className="text-white/80 text-sm sm:text-base max-w-2xl mx-auto leading-relaxed">
                Clear, transparent, and enterprise-grade contractual standards governing our performance marketing, web engineering, and AI automation partnerships.
              </p>
              <div className="pt-2 text-xs font-mono text-white/50">
                Effective Date & Last Updated: {lastUpdated}
              </div>
            </div>

            {/* Quick Principles Banner */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="glass-panel p-5 border border-white/10 space-y-2">
                <div className="w-8 h-8 rounded-lg bg-[#F3453F]/20 flex items-center justify-center text-[#F3453F]">
                  <ShieldCheck size={18} />
                </div>
                <p className="font-bold text-white text-sm">Transparent Invoicing</p>
                <p className="text-xs text-white/90">Ad spend billed directly to networks; zero hidden markups or blended fee opacity.</p>
              </div>
              <div className="glass-panel p-5 border border-white/10 space-y-2">
                <div className="w-8 h-8 rounded-lg bg-[#F3453F]/20 flex items-center justify-center text-[#F3453F]">
                  <Award size={18} />
                </div>
                <p className="font-bold text-white text-sm">Full Asset Ownership</p>
                <p className="text-xs text-white/90">Clients retain 100% administrative control over ad accounts, tracking data, and CRM leads.</p>
              </div>
              <div className="glass-panel p-5 border border-white/10 space-y-2">
                <div className="w-8 h-8 rounded-lg bg-[#F3453F]/20 flex items-center justify-center text-[#F3453F]">
                  <Briefcase size={18} />
                </div>
                <p className="font-bold text-white text-sm">Senior Strategic Governance</p>
                <p className="text-xs text-white/90">Direct executive strategy oversight led by Founder Sahil Sharma on all client accounts.</p>
              </div>
            </div>

            {/* Detailed Terms Sections */}
            <div className="space-y-8">
              {termsSections.map((sec) => {
                const Icon = sec.icon;
                return (
                  <motion.section
                    key={sec.id}
                    id={sec.id}
                    initial={{ opacity: 0, y: 15 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="glass-panel p-6 sm:p-8 space-y-4 border border-white/10 hover:border-white/20 transition-colors"
                  >
                    <div className="flex items-center gap-3 border-b border-white/10 pb-4">
                      <div className="w-10 h-10 rounded-xl bg-[#F3453F]/15 border border-[#F3453F]/40 flex items-center justify-center text-[#F3453F]">
                        <Icon size={20} />
                      </div>
                      <h2 className="text-xl sm:text-2xl font-bold text-white font-heading">
                        {sec.title}
                      </h2>
                    </div>
                    <div className="text-white/85 text-sm sm:text-base leading-relaxed font-sans">
                      {sec.content}
                    </div>
                  </motion.section>
                );
              })}
            </div>

            {/* Bottom Contact Callout */}
            <div className="p-6 rounded-2xl bg-[#202942] border border-white/15 text-center space-y-3">
              <p className="text-lg font-bold text-white font-heading">Need custom enterprise MSA or contract terms?</p>
              <p className="text-xs sm:text-sm text-white/90 max-w-xl mx-auto">
                For custom statements of work, vendor onboarding documents, or enterprise master service agreements, contact <a href="mailto:connect@stellarsestates.in" className="text-[#F3453F] font-bold hover:underline min-h-[44px] inline-flex items-center">connect@stellarsestates.in</a>.
              </p>
            </div>

          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default TermsOfServicePage;
